import clsx from "clsx"
import { PropsWithChildren, ReactNode, isValidElement, cloneElement } from "react"
import { Control, Controller, ControllerProps, useFormContext } from "react-hook-form"

type Props = PropsWithChildren<{
  label?: ReactNode
  required?: boolean
  disabled?: boolean
  error?: string
  hideErrorMessage?: boolean
  note?: ReactNode
  className?: string
  grow?: boolean

  // support for react-hook-form...
  // https://github.com/radix-ui/primitives/issues/2590
  // (basically use Controller here with render (as done by shadui) instead of registering the field directly because radix doesn't support ref forwarding and the onChange events don't match)
  name?: string
  render?: ControllerProps["render"]
  control?: Control<any> // https://github.com/react-hook-form/react-hook-form/issues/4965
}>

export function Field(props: Props) {
  const { label, note, children, className, error, grow, hideErrorMessage, ...additionalProps } = props

  const formMethods = useFormContext()
  const fieldState = props.name ? formMethods?.getFieldState(props.name, formMethods.formState) : undefined

  const errored = props.error || fieldState?.error?.message

  return (
    <div
      className={clsx(
        "flex flex-col gap-1",
        errored ? "error" : "",
        !!props.disabled ? "disabled" : "",
        grow ? "flex-grow stretch-fields" : "",
        className,
      )}
    >
      {!!label && (
        <label
          className="type-ui-s-roman-caps text-grey-dark [.error_&]:text-error-dark [.disabled_&]:text-grey-mid"
          data-part="label"
        >
          {label}
          {props.required && <span data-part="required">*</span>}
        </label>
      )}
      <div data-part="input">
        {!!props.render && !!props.name ? (
          <Controller name={props.name} render={props.render} control={props.control} />
        ) : isValidElement(children) ? (
          cloneElement(children as React.ReactElement, additionalProps)
        ) : null}
      </div>
      {!!note || (!!errored && !hideErrorMessage) ? (
        <div className="type-body-s-roman text-grey-dark error:text-error-dark [.disabled_&]:text-grey-mid">
          {errored || note}
        </div>
      ) : null}
    </div>
  )
}
